import { api } from './client';

export interface ZigRate {
  id?: number;
  rate: number;
  effectiveDate?: string;
  createdBy?: string;
  updatedBy?: string;
  notes?: string;
  isActive?: boolean;
}

export interface DashboardMetrics {
  totalClients: number;
  totalLoans: number;
  activeLoans: number;
  totalDisbursed: number;
  totalOutstanding: number;
  activeZigRate?: {
    rate: number;
    effectiveDate: string;
  };
}

// ZIG Rate Management
export const adminApi = {
  // ZIG Rates
  getActiveZigRate: async (): Promise<ZigRate | null> => {
    const res = await api.get('/api/admin/zig-rates/active');
    return res.data || null;
  },

  getAllZigRates: async (): Promise<ZigRate[]> => {
    const res = await api.get('/api/admin/zig-rates');
    return res.data || [];
  },

  getZigRateById: async (id: number): Promise<ZigRate> => {
    const res = await api.get(`/api/admin/zig-rates/${id}`);
    return res.data;
  },

  createZigRate: async (rate: number, notes?: string): Promise<ZigRate> => {
    const res = await api.post('/api/admin/zig-rates', { rate, notes });
    return res.data;
  },

  createZigRateForDate: async (rate: number, effectiveDate: string, notes?: string): Promise<ZigRate> => {
    const res = await api.post('/api/admin/zig-rates/historical', { rate, effectiveDate, notes });
    return res.data;
  },

  bulkUploadZigRates: async (rates: Array<{ rate: number; date: string; notes?: string }>): Promise<{ count: number; rates: ZigRate[] }> => {
    // Convert date to effectiveDate format expected by backend
    const formattedRates = rates.map(r => ({
      rate: r.rate,
      effectiveDate: r.date,
      notes: r.notes
    }));
    const res = await api.post('/api/admin/zig-rates/bulk-upload', { rates: formattedRates });
    return res.data;
  },

  updateZigRate: async (id: number, rate: number, notes?: string): Promise<ZigRate> => {
    const res = await api.put(`/api/admin/zig-rates/${id}`, { rate, notes });
    return res.data;
  },

  // Clients
  getAllClients: async (): Promise<any[]> => {
    const res = await api.get('/api/admin/clients');
    return res.data || [];
  },

  getClientDetails: async (clientId: string): Promise<any> => {
    const res = await api.get(`/api/admin/clients/${clientId}`);
    return res.data;
  },

  getClientAccounts: async (clientId: string): Promise<any> => {
    const res = await api.get(`/api/admin/clients/${clientId}/accounts`);
    return res.data;
  },

  // Metrics
  getDashboardMetrics: async (): Promise<DashboardMetrics> => {
    const res = await api.get('/api/admin/metrics');
    return res.data;
  },

  // Operations
  startOfDay: async (): Promise<any> => {
    const res = await api.post('/api/admin/operations/start-of-day');
    return res.data;
  },

  endOfDay: async (): Promise<any> => {
    const res = await api.post('/api/admin/operations/end-of-day');
    return res.data;
  },

  closeOfBusiness: async (): Promise<any> => {
    const res = await api.post('/api/admin/operations/close-of-business');
    return res.data;
  },
};

// Audit Logs
export interface AuditLog {
  id?: number;
  userId?: string;
  username?: string;
  action: string;
  entityType?: string;
  entityId?: number;
  details?: string;
  ipAddress?: string;
  userAgent?: string;
  success?: boolean;
  errorMessage?: string;
  timestamp?: string;
  createdAt?: string;
}

export interface AuditLogsResponse {
  content: AuditLog[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

export interface AuditStatistics {
  totalLogs: number;
  byAction: Array<[string, number]>;
  byEntityType: Array<[string, number]>;
  byUser: Array<[string, number]>;
  successCount: number;
  failureCount: number;
}

export const auditApi = {
  getAuditLogs: async (params?: {
    userId?: string;
    action?: string;
    entityType?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    size?: number;
  }): Promise<AuditLogsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.userId) queryParams.append('userId', params.userId);
    if (params?.action) queryParams.append('action', params.action);
    if (params?.entityType) queryParams.append('entityType', params.entityType);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    
    const url = `/api/admin/audit/logs${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const res = await api.get(url);
    return res.data;
  },

  getAuditStatistics: async (): Promise<AuditStatistics> => {
    const res = await api.get('/api/admin/audit/statistics');
    return res.data;
  },

  getAuditLogsForEntity: async (entityType: string, entityId: number): Promise<AuditLog[]> => {
    const res = await api.get(`/api/admin/audit/entity/${entityType}/${entityId}`);
    return res.data;
  },
};

// Product Management
export interface Product {
  id?: number;
  productName: string;
  applicationFeeDescription?: string;
  applicationFeePercentage?: number;
  applicationFeeMinimum?: number;
  applicationFeeMaximum?: number;
  loanTenureDescription?: string;
  loanTenureMonths?: number;
  /** Minimum allowed tenure in months (from Skyfin minNumberOfRepayments when available) */
  minLoanTenureMonths?: number;
  /** Maximum allowed tenure in months (from Skyfin maxNumberOfRepayments when available) */
  maxLoanTenureMonths?: number;
  currency?: string;
  interestRateDescription?: string;
  interestRatePercentage?: number;
  interestRateEffectiveDate?: string;
  administrationFeesDescription?: string;
  administrationFeesPercentage?: number;
  serviceFeesDescription?: string;
  serviceFeesAmount?: number;
  minimumPrincipal?: number;
  maximumPrincipal?: number;
  startDate?: string;
  closeDate?: string;
  amortization?: string;
  interestMethod?: string;
  interestCalculationPeriod?: string;
  charges?: string;
  otherDetails?: string;
  formConfig?: any; // JSON configuration for product-specific form fields
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  fineractProductId?: number;
  shortName?: string;
  source?: 'LOCAL' | 'SKYFIN' | string;
  /** Female | Male | omit/null = all genders */
  eligibleGender?: 'Female' | 'Male' | string | null;
}

export const productApi = {
  getAllProducts: async (): Promise<Product[]> => {
    const res = await api.get('/api/v1/admin/products');
    return res.data || [];
  },

  getActiveProducts: async (): Promise<Product[]> => {
    const res = await api.get('/api/v1/admin/products/active');
    return res.data || [];
  },

  getProductById: async (id: number): Promise<Product> => {
    const res = await api.get(`/api/v1/admin/products/${id}`);
    return res.data;
  },

  createProduct: async (product: Product): Promise<Product> => {
    const res = await api.post('/api/v1/admin/products', product);
    return res.data;
  },

  updateProduct: async (id: number, product: Product): Promise<Product> => {
    const res = await api.put(`/api/v1/admin/products/${id}`, product);
    return res.data;
  },

  deleteProduct: async (id: number): Promise<void> => {
    await api.delete(`/api/v1/admin/products/${id}`);
  },

  /** Loan products from Skyfin (Fineract), mapped to Product shape */
  getSkyfinProducts: async (): Promise<Product[]> => {
    const res = await api.get('/api/v1/admin/products/skyfin');
    return res.data || [];
  },

  getActiveSkyfinProducts: async (): Promise<Product[]> => {
    const res = await api.get('/api/v1/admin/products/skyfin/active');
    return res.data || [];
  },

  getSkyfinProductById: async (productId: number): Promise<Product> => {
    const res = await api.get(`/api/v1/admin/products/skyfin/${productId}`);
    return res.data;
  },

  /** Upsert Skyfin loan products into local catalog (keeps formConfig) */
  syncSkyfinProducts: async (): Promise<{ message: string; count: number; products: Product[] }> => {
    const res = await api.post('/api/v1/admin/products/skyfin/sync');
    return res.data;
  },

  getSkyfinSavingsProducts: async (): Promise<any[]> => {
    const res = await api.get('/api/v1/admin/products/skyfin/savings');
    return res.data || [];
  },
};

